import { volumeGainAt, volumeDbToGain, type VolumePoint } from '@/types/timeline-editing'
/**
 * Audio cache for video playback
 * Pre-decodes audio samples from video assets using Mediabunny + Web Audio API
 *
 * Architecture (December 2024):
 * - Uses Mediabunny's AudioSampleSink for WebCodecs-based audio decoding
 * - Converts AudioSamples to AudioBuffers and properly closes samples
 * - Stores decoded AudioBuffers for instant playback
 * - Uses Web Audio API for scheduling audio during playback
 * - LRU eviction based on memory budget from manual limit
 */

import { LRUCache } from 'lru-cache'
import { Input, AudioSampleSink, UrlSource, ALL_FORMATS } from 'mediabunny'
import { getFetchableStorageUrl } from './fetchableStorageUrl'
import { getSinkUrl } from './videoCache'
import {
  isTimelinePlaybackActive,
  recordPlaybackBackgroundWork,
  schedulePlaybackAwareBackgroundWork,
} from './playbackActivity'
import { decodeAudioWithFallback } from './audioDecodeFallback'
import { clampAudioFades } from '@/types/shared'

/** Type for audio track from Mediabunny Input */
type AudioTrack = NonNullable<Awaited<ReturnType<Input['getPrimaryAudioTrack']>>>
import { SoundTouch, SimpleFilter, WebAudioBufferSource } from 'soundtouchjs'
import { getAudioCacheBudgetMB } from './gpuDetection'

interface AudioBufferEntry {
  buffer: AudioBuffer
  timestamp: number
  duration: number
}

export interface AudioRange {
  start: number
  end: number
}

interface PartialAudio {
  buffers: AudioBufferEntry[]
  coverage: AudioRange[]
  sampleRate: number
  channels: number
  duration: number
}

/** Pre-processed tempo buffers for pitch-preserved playback */
interface TempoBuffers {
  /** Combined buffer processed at this tempo (pitch preserved) */
  buffer: AudioBuffer
  /** Same but reversed for reverse playback */
  reversedBuffer: AudioBuffer
}

interface CachedAudio {
  buffers: AudioBufferEntry[]
  reversedBuffers: AudioBufferEntry[] // Pre-reversed for reverse playback
  /** Pre-processed tempo buffers: tempo -> processed audio */
  tempoBuffers: Map<number, TempoBuffers>
  sampleRate: number
  channels: number
  duration: number
  memoryBytes: number // Track memory usage for LRU eviction
}

interface AudioCacheStats {
  totalVideos: number
  totalBuffers: number
  totalDurationSeconds: number
  totalMemoryMB: number
  memoryBudgetMB: number
  utilizationPercent: number
}

function isInputDisposedError(error: unknown): boolean {
  return error instanceof Error && error.name === 'InputDisposedError'
}

// Global audio cache with LRU eviction
let audioCache: LRUCache<string, CachedAudio>

// Track if cache has been initialized
let audioCacheInitialized = false

// Track if memory limit change listener is registered
let audioMemoryLimitListenerRegistered = false

// Track which videoIds have been warned about (log once per session, not per call)
const warnedMissingAudioIds = new Set<string>()

// Track videos that have no audio track (to avoid repeated decode attempts)
const videosWithoutAudio = new Set<string>()

// Progressive timeline decodes are playable before they are complete, but stay outside the LRU.
const partialDecodes = new Map<string, PartialAudio>()

const audioCacheChangeListeners = new Set<() => void>()
let audioCacheChangeNotificationQueued = false
let audioProgressNotificationTimer: ReturnType<typeof setTimeout> | null = null

function notifyAudioCacheChanged(): void {
  if (audioCacheChangeNotificationQueued) return
  audioCacheChangeNotificationQueued = true
  queueMicrotask(() => {
    audioCacheChangeNotificationQueued = false
    for (const listener of audioCacheChangeListeners) listener()
  })
}

function notifyAudioProgress(): void {
  if (audioProgressNotificationTimer) return
  // ponytail: tune the throttle if readiness recomputation becomes measurable under bursty decodes.
  audioProgressNotificationTimer = setTimeout(() => {
    audioProgressNotificationTimer = null
    for (const listener of audioCacheChangeListeners) listener()
  }, 100)
}

export function subscribeAudioCacheChange(listener: () => void): () => void {
  audioCacheChangeListeners.add(listener)
  return () => audioCacheChangeListeners.delete(listener)
}

/** Check if a video is known to have no audio track (discovered during decode) */
export function isKnownNoAudio(assetId: string): boolean {
  return videosWithoutAudio.has(assetId)
}

// Decoded chunks carry WebCodecs AudioData timing: integer microseconds. A 1024-frame AAC chunk at
// 48 kHz is 21333.33 µs, so consecutive chunks overlap or miss each other by a fraction of a
// microsecond in either direction. Range math treats anything within this slack as touching.
const AUDIO_RANGE_EPSILON_SECONDS = 0.001

/** Insert a source range while keeping the list sorted and coalescing touching ranges. */
export function mergeRange(ranges: AudioRange[], range: AudioRange): AudioRange[] {
  if (range.end <= range.start) return ranges

  let start = range.start
  let end = range.end
  let insertAt = 0
  while (insertAt < ranges.length && ranges[insertAt].end + AUDIO_RANGE_EPSILON_SECONDS < start) insertAt++

  let removeThrough = insertAt
  while (removeThrough < ranges.length && ranges[removeThrough].start <= end + AUDIO_RANGE_EPSILON_SECONDS) {
    start = Math.min(start, ranges[removeThrough].start)
    end = Math.max(end, ranges[removeThrough].end)
    removeThrough++
  }

  ranges.splice(insertAt, removeThrough - insertAt, { start, end })
  return ranges
}

/** 'complete' when fully cached, null when nothing decoded yet (or known-no-audio), else decoded ranges. */
// Audio tracks often end a few ms before the video, and sample timestamps carry float error, so a
// clip's source range can never be "covered" exactly at the tail. A range that reaches the track's
// end is reported as open-ended.
const COVERAGE_TAIL_TOLERANCE_SECONDS = 0.05

export function getAudioCoverage(assetId: string): AudioRange[] | 'complete' | null {
  ensureAudioCacheInitialized()
  if (audioCache.has(assetId)) return 'complete'
  const partial = partialDecodes.get(assetId)
  if (!partial || partial.coverage.length === 0) return null
  return partial.coverage.map(range =>
    range.end >= partial.duration - COVERAGE_TAIL_TOLERANCE_SECONDS
      ? { start: range.start, end: Number.POSITIVE_INFINITY }
      : range,
  )
}

/** True when the requested half-open source range is wholly decoded. */
export function isAudioCoveredRange(assetId: string, fromSeconds: number, toSeconds: number): boolean {
  if (toSeconds <= fromSeconds) return true
  const coverage = getAudioCoverage(assetId)
  if (coverage === 'complete') return true
  return (
    coverage?.some(
      range =>
        range.start <= fromSeconds + AUDIO_RANGE_EPSILON_SECONDS &&
        range.end >= toSeconds - AUDIO_RANGE_EPSILON_SECONDS,
    ) ?? false
  )
}

// Flag to pause audio scheduling during resize/drag operations
// When true, scheduleAudioInWindow returns early without scheduling any audio
let audioSchedulingPaused = false

/**
 * Estimate memory usage for an AudioBuffer
 * AudioBuffers use Float32Array (4 bytes per sample per channel)
 */
function estimateAudioBufferMemory(buffer: AudioBuffer): number {
  return buffer.length * buffer.numberOfChannels * 4 // Float32 = 4 bytes
}

/**
 * Initialize the audio cache with budget from manual memory limit
 */
function ensureAudioCacheInitialized(): void {
  if (audioCacheInitialized) return

  const budgetMB = getAudioCacheBudgetMB()
  const budgetBytes = budgetMB * 1024 * 1024

  audioCache = new LRUCache<string, CachedAudio>({
    maxSize: budgetBytes,
    sizeCalculation: cached => cached.memoryBytes,
    dispose: () => notifyAudioCacheChanged(),
    updateAgeOnGet: true,
  })

  audioCacheInitialized = true

  // Register listener for budget changes
  registerAudioMemoryLimitListener()
}

/**
 * Update the audio cache budget (called when manual memory limit changes)
 */
function updateAudioCacheBudget(): void {
  const newBudgetMB = getAudioCacheBudgetMB()
  const newBudgetBytes = newBudgetMB * 1024 * 1024

  if (!audioCache) {
    return
  }

  // Save existing entries
  const existingEntries: Array<[string, CachedAudio]> = []
  for (const [key, value] of audioCache.entries()) {
    existingEntries.push([key, value])
  }

  // Create new cache with updated budget
  audioCache = new LRUCache<string, CachedAudio>({
    maxSize: newBudgetBytes,
    sizeCalculation: cached => cached.memoryBytes,
    dispose: () => notifyAudioCacheChanged(),
    updateAgeOnGet: true,
  })

  // Transfer existing entries (LRU will evict if they don't fit)
  for (const [key, value] of existingEntries) {
    audioCache.set(key, value)
    notifyAudioCacheChanged()
  }
}

/**
 * Register listener for memory limit changes
 */
function registerAudioMemoryLimitListener(): void {
  if (audioMemoryLimitListenerRegistered || typeof window === 'undefined') return

  window.addEventListener('memory-limit-changed', () => {
    updateAudioCacheBudget()
  })
  audioMemoryLimitListenerRegistered = true
}

// Track videos currently being decoded
const pendingDecodes = new Map<string, Promise<CachedAudio | null>>()

interface PendingTempoBuild {
  /** The scheduler needs this buffer for the playback in progress; it must not wait for playback to stop. */
  urgent: boolean
  cancelDeferred: (() => void) | null
}

const pendingTempoBuilds = new Map<string, PendingTempoBuild>()

/**
 * Idle-callback deadline for a build the current playback is waiting on. Long enough to let a
 * frame render first, short enough that the clip is audible within a couple of scheduler passes.
 */
const URGENT_TEMPO_BUILD_TIMEOUT_MS = 250
const BACKGROUND_TEMPO_BUILD_TIMEOUT_MS = 10000

/**
 * Return the tempo buffer when it exists; otherwise start its build and return undefined.
 *
 * Background requests (pre-warming) are deferred while the timeline plays, like every other heavy
 * audio job. Urgent requests come from the scheduler for a clip that should be audible *now*: the
 * deferred queue only drains after playback stops, so parking them there would leave the clip
 * silent for the whole playback. They run at the next idle slot instead, and take over a
 * background build already waiting behind playback.
 */
function requestTempoBuffer(assetId: string, tempo: number, urgent: boolean): TempoBuffers | undefined {
  tempo = Math.round(tempo * 10000) / 10000
  const cached = audioCache.peek(assetId)
  if (!cached) return undefined
  const existing = cached.tempoBuffers.get(tempo)
  if (existing) return existing
  const key = `${assetId}:${tempo}`
  const pending = pendingTempoBuilds.get(key)
  if (pending && (pending.urgent || !urgent)) return undefined
  pending?.cancelDeferred?.()
  const entry: PendingTempoBuild = { urgent, cancelDeferred: null }
  pendingTempoBuilds.set(key, entry)
  const build = () => {
    // A superseded background build must not run alongside the urgent one that replaced it.
    if (pendingTempoBuilds.get(key) !== entry) return
    if (!urgent && isTimelinePlaybackActive()) {
      entry.cancelDeferred = schedulePlaybackAwareBackgroundWork(`audio-tempo:${key}`, 'audio-heavy-processing', build)
      return
    }
    try {
      // Do not resurrect an evicted or replaced entry.
      if (audioCache.peek(assetId) !== cached || cached.tempoBuffers.has(tempo)) return
      const ctx = getAudioContext()
      const source = combineBufferChunks(ctx, cached.buffers, cached.duration, cached.sampleRate, cached.channels)
      const buffer = processBufferWithTempo(ctx, source, tempo)
      const reversedBuffer = reverseAudioBuffer(buffer)
      cached.tempoBuffers.set(tempo, { buffer, reversedBuffer })
      cached.memoryBytes += estimateAudioBufferMemory(buffer) + estimateAudioBufferMemory(reversedBuffer)
      audioCache.set(assetId, cached)
      notifyAudioCacheChanged()
    } catch (error) {
      console.error(`[AudioCache] Error during tempo processing for asset ${assetId}:`, error)
    } finally {
      pendingTempoBuilds.delete(key)
    }
  }
  const runWhenIdle = () => {
    if (typeof requestIdleCallback !== 'undefined')
      requestIdleCallback(build, {
        timeout: urgent ? URGENT_TEMPO_BUILD_TIMEOUT_MS : BACKGROUND_TEMPO_BUILD_TIMEOUT_MS,
      })
    else setTimeout(build, urgent ? 0 : 500)
  }
  if (urgent) {
    if (isTimelinePlaybackActive())
      recordPlaybackBackgroundWork('audio-heavy-processing', 'playback-critical', { reason: 'tempo-buffer-missing' })
    runWhenIdle()
  } else {
    entry.cancelDeferred = schedulePlaybackAwareBackgroundWork(
      `audio-tempo:${key}`,
      'audio-heavy-processing',
      runWhenIdle,
    )
  }
  return undefined
}

/**
 * Build a tempo buffer ahead of playback, in idle time. Pitch-preserved retimed clips play from
 * one of these, so the playback warmer asks for each clip's speed once its audio is decoded.
 */
export function prewarmTempoBuffer(assetId: string, tempo: number): void {
  ensureAudioCacheInitialized()
  requestTempoBuffer(assetId, tempo, false)
}

/**
 * Schedule heavy audio processing (reverse buffers, tempo buffers) during browser idle time.
 * This prevents blocking the UI during audio upload while still enabling playback features later.
 *
 * Processing order:
 * 1. Reverse buffer (enables reverse playback at 1x)
 * 2. Tempo buffers (enables 2x playback)
 *
 * @param assetId - The asset ID to process
 * @param sourceBuffer - The source AudioBuffer to process (must be a copy, not original)
 * @param isAudioOnly - True for audio-only files (single buffer), false for video audio (chunked)
 */
function scheduleHeavyAudioProcessing(assetId: string, sourceBuffer: AudioBuffer, isAudioOnly: boolean): void {
  const key = `audio-heavy-processing:${assetId}`
  const processBuffers = () => {
    if (isTimelinePlaybackActive()) {
      schedulePlaybackAwareBackgroundWork(key, 'audio-heavy-processing', processBuffers)
      return
    }

    try {
      // Check if asset is still in cache (might have been evicted)
      const cached = audioCache.peek(assetId)
      if (!cached) {
        return
      }

      const ctx = getAudioContext()

      // Step 1: Create reversed buffers if not already done (for audio-only files).
      if (isAudioOnly) getReversedBuffers(assetId, cached)

      // Step 2: Create tempo buffers if not already done
      for (const tempo of PREPROCESS_TEMPOS) {
        if (cached.tempoBuffers.has(tempo)) continue
        const processedBuffer = processBufferWithTempo(ctx, sourceBuffer, tempo)
        const reversedProcessed = reverseAudioBuffer(processedBuffer)

        cached.tempoBuffers.set(tempo, {
          buffer: processedBuffer,
          reversedBuffer: reversedProcessed,
        })

        cached.memoryBytes += estimateAudioBufferMemory(processedBuffer)
        cached.memoryBytes += estimateAudioBufferMemory(reversedProcessed)
      }

      // Re-set the cache entry to update LRU size tracking
      // (lru-cache only calculates size on .set(), not when mutating the entry)
      audioCache.set(assetId, cached)
      notifyAudioCacheChanged()
    } catch (error) {
      console.error(`[AudioCache] Error during heavy processing for asset ${assetId}:`, error)
    }
  }

  if (isTimelinePlaybackActive()) {
    schedulePlaybackAwareBackgroundWork(key, 'audio-heavy-processing', processBuffers)
    return
  }

  // Use requestIdleCallback to process during idle time, with a timeout fallback
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(processBuffers, { timeout: 10000 }) // 10s max delay
  } else {
    // Safari fallback - use setTimeout with a delay to let UI settle
    setTimeout(processBuffers, 500)
  }
}

/**
 * Legacy wrapper for video audio (backward compatibility)
 */
function scheduleTempoProcessing(assetId: string, sourceBuffer: AudioBuffer): void {
  scheduleHeavyAudioProcessing(assetId, sourceBuffer, false)
}

// Global AudioContext (created lazily on first use)
let audioContext: AudioContext | null = null

// Master gain node for O(1) audio stop (disconnect once instead of per-node)
let masterGainNode: GainNode | null = null

// Scheduled audio nodes per player instance (for scoped stopping)
const scheduledNodesByPlayer = new Map<string, AudioBufferSourceNode[]>()

// Legacy global list for backwards compatibility
let scheduledNodes: AudioBufferSourceNode[] = []

// Track which clips have been scheduled (by unique key: assetId + startTicks)
// Used by windowed scheduling to avoid re-scheduling the same clip
const scheduledClipKeys = new Set<string>()

/**
 * Metadata for every audio source scheduled for one clip, for cleanup by position.
 */
interface ScheduledNodeInfo {
  nodes: AudioBufferSourceNode[]
  clipKey: string
  clipStartSeconds: number
  clipEndSeconds: number
  /** Source timestamps already scheduled for this clip; chunks decode out of order (trim-point pass first). */
  scheduledSourceTimestamps: Set<number>
  complete: boolean
}

// Extended tracking for windowed cleanup
let scheduledNodeInfos: ScheduledNodeInfo[] = []

/**
 * Get or create AudioContext
 */
export function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new AudioContext()
  }
  return audioContext
}

/**
 * Get or create the master gain node.
 * All audio sources connect through this for O(1) stop (disconnect once instead of per-node).
 */
function getMasterGain(): GainNode {
  if (!masterGainNode) {
    const ctx = getAudioContext()
    masterGainNode = ctx.createGain()
    masterGainNode.connect(ctx.destination)
  }
  return masterGainNode
}

/**
 * Resume AudioContext if suspended (required for autoplay policy)
 */
export async function resumeAudioContext(): Promise<void> {
  const ctx = getAudioContext()
  if (ctx.state === 'suspended') {
    await ctx.resume()
  }
}

/**
 * Get cache statistics
 */
export function getAudioCacheStats(): AudioCacheStats {
  ensureAudioCacheInitialized()

  let totalBuffers = 0
  let totalDurationSeconds = 0

  for (const cached of audioCache.values()) {
    totalBuffers += cached.buffers.length
    totalDurationSeconds += cached.duration
  }

  const totalMemoryBytes = audioCache.calculatedSize ?? 0
  const budgetMB = getAudioCacheBudgetMB()
  const budgetBytes = budgetMB * 1024 * 1024

  return {
    totalVideos: audioCache.size,
    totalBuffers,
    totalDurationSeconds,
    totalMemoryMB: Math.round((totalMemoryBytes / 1024 / 1024) * 100) / 100,
    memoryBudgetMB: budgetMB,
    utilizationPercent: budgetBytes > 0 ? Math.round((totalMemoryBytes / budgetBytes) * 100) : 0,
  }
}

/**
 * Check if audio is cached for a video
 */
export function hasAudioCache(videoId: string): boolean {
  ensureAudioCacheInitialized()
  return audioCache.has(videoId)
}

/**
 * Check if audio is being decoded for a video
 */
export function isDecodingAudio(videoId: string): boolean {
  return pendingDecodes.has(videoId)
}

/**
 * Get count of active audio decode operations
 */
export function getActiveDecodeCount(): number {
  return pendingDecodes.size
}

/**
 * Get list of all cached video IDs (for debug overlay)
 */
export function getCachedVideoIds(): string[] {
  ensureAudioCacheInitialized()
  return Array.from(audioCache.keys())
}

/**
 * Get list of video IDs currently being decoded (for debug overlay)
 */
export function getDecodingVideoIds(): string[] {
  return Array.from(pendingDecodes.keys())
}

/**
 * Pre-decode all audio from a video URL
 */
export async function preloadAudio(
  videoId: string,
  videoUrl: string,
  onProgress?: (progress: number) => void,
  options: { fromSeconds?: number } = {},
): Promise<CachedAudio | null> {
  ensureAudioCacheInitialized()

  // Check if already cached (LRU.get() updates access time)
  const existingCached = audioCache.get(videoId)
  if (existingCached) {
    return existingCached
  }

  // Skip videos known to have no audio track
  if (videosWithoutAudio.has(videoId)) {
    return null
  }

  // Check if already being decoded
  const pendingDecode = pendingDecodes.get(videoId)
  if (pendingDecode) {
    return pendingDecode
  }

  const decodePromise = decodeAudioViaMediAbunny(videoId, videoUrl, onProgress, options.fromSeconds)
  pendingDecodes.set(videoId, decodePromise)

  try {
    return await decodePromise
  } finally {
    pendingDecodes.delete(videoId)
  }
}

/**
 * Pre-decode audio from a pure audio file (MP3, M4A, WAV, etc.) using Web Audio API.
 * This is used for audio-only assets where Mediabunny (video-focused) doesn't work well.
 *
 * Uses AudioContext.decodeAudioData() which natively supports common audio formats.
 *
 * @param assetId The audio asset ID
 * @param audioUrl URL to the audio file (proxy or original)
 * @param options Optional progress callback and original-file fallback URL
 * @returns Cached audio or null on failure
 */
export async function preloadAudioFile(
  assetId: string,
  audioUrl: string,
  options: {
    onProgress?: (progress: number) => void
    fallbackUrl?: string
  } = {},
): Promise<CachedAudio | null> {
  ensureAudioCacheInitialized()

  // Check if already cached
  const existingCached = audioCache.get(assetId)
  if (existingCached) {
    return existingCached
  }

  // Check if already being decoded
  const pendingDecode = pendingDecodes.get(assetId)
  if (pendingDecode) {
    return pendingDecode
  }

  const decodePromise = decodeAudioFileViaWebAudio(assetId, audioUrl, options)
  pendingDecodes.set(assetId, decodePromise)

  try {
    return await decodePromise
  } finally {
    pendingDecodes.delete(assetId)
  }
}

// Shared background audio lane. Playback keeps it 1-wide so the warmer can decode the
// next needed asset without competing with the active video sink; idle time uses both slots.
const BACKGROUND_PRELOAD_MAX_CONCURRENT = 2
const BACKGROUND_PRELOAD_SLOT_DEADLINE_MS = 60_000
let backgroundPreloadsInFlight = 0

/** A queued-or-running preload job, ref-counted across callers that share the same asset. */
interface BackgroundPreloadEntry {
  promise: Promise<CachedAudio | null>
  resolve: (value: CachedAudio | null) => void
  run: () => void
  started: boolean
  priority: number
  refs: number
  abortHandlers: Array<{ signal: AbortSignal; handler: () => void }>
  onDecoded: Array<(audio: CachedAudio) => void>
}
const backgroundPreloadQueue: BackgroundPreloadEntry[] = []
const backgroundPreloadEntries = new Map<string, BackgroundPreloadEntry>()

function detachAbortHandlers(entry: BackgroundPreloadEntry): void {
  for (const { signal, handler } of entry.abortHandlers) {
    signal.removeEventListener('abort', handler)
  }
  entry.abortHandlers.length = 0
}

function pumpBackgroundPreloads(): void {
  const maxConcurrent = isTimelinePlaybackActive() ? 1 : BACKGROUND_PRELOAD_MAX_CONCURRENT
  while (backgroundPreloadsInFlight < maxConcurrent && backgroundPreloadQueue.length > 0) {
    let nextIndex = 0
    for (let index = 1; index < backgroundPreloadQueue.length; index++) {
      if (backgroundPreloadQueue[index].priority < backgroundPreloadQueue[nextIndex].priority) {
        nextIndex = index
      }
    }
    backgroundPreloadQueue.splice(nextIndex, 1)[0].run()
  }
}

export function setBackgroundAudioPreloadPriority(assetId: string, priority: number): void {
  const entry = backgroundPreloadEntries.get(assetId)
  if (!entry || entry.started || priority >= entry.priority) return
  entry.priority = priority
}

// A caller drops its interest in a queued asset (e.g. its clip unmounted). Once every
// caller has dropped out, and the job hasn't started, it gives up its place in the lane.
// An in-flight decode is never cancelled — only queued-but-unstarted jobs are.
function releaseBackgroundPreload(assetId: string, entry: BackgroundPreloadEntry): void {
  entry.refs--
  if (entry.refs > 0 || entry.started) return
  const queueIndex = backgroundPreloadQueue.indexOf(entry)
  if (queueIndex !== -1) backgroundPreloadQueue.splice(queueIndex, 1)
  backgroundPreloadEntries.delete(assetId)
  detachAbortHandlers(entry)
  entry.resolve(null)
}

/**
 * Preload an asset's audio for waveform generation without hurting playback.
 * Video-backed clips go through the audio-track-only decoder (range reads of the audio
 * samples) instead of downloading and decoding the whole proxy file; pure audio files use
 * the Web Audio decoder. At most two decodes run at once.
 */
export function queueBackgroundAudioPreload(
  assetId: string,
  mediaUrl: string,
  options: {
    audioOnly: boolean
    fallbackUrl?: string
    signal?: AbortSignal
    onDecoded?: (audio: CachedAudio) => void
    priority?: number
    fromSeconds?: number
  },
): Promise<CachedAudio | null> {
  ensureAudioCacheInitialized()
  if (options.signal?.aborted) return Promise.resolve(null)

  const cached = audioCache.peek(assetId)
  if (cached) {
    options.onDecoded?.(cached)
    return Promise.resolve(cached)
  }
  const pendingDecode = pendingDecodes.get(assetId)
  if (pendingDecode) {
    return pendingDecode.then(result => {
      if (result) options.onDecoded?.(result)
      return result
    })
  }

  const existing = backgroundPreloadEntries.get(assetId)
  if (existing) {
    existing.refs++
    if (!existing.started) {
      existing.priority = Math.min(existing.priority, options.priority ?? Number.POSITIVE_INFINITY)
    }
    if (options.onDecoded) existing.onDecoded.push(options.onDecoded)
    if (options.signal) {
      const handler = () => releaseBackgroundPreload(assetId, existing)
      options.signal.addEventListener('abort', handler, { once: true })
      existing.abortHandlers.push({ signal: options.signal, handler })
    }
    return existing.promise
  }
  if (!options.audioOnly && videosWithoutAudio.has(assetId)) return Promise.resolve(null)

  // Decide by the container, not the asset type: the timeline hands audio-track clips a
  // video asset re-typed as audio, and the Web Audio path would then download the whole
  // video file just to decode its audio track.
  const useWebAudio = options.audioOnly && !/\.(mp4|m4v|mov|webm|mkv)(?:[?#]|$)/i.test(mediaUrl)
  let resolvePromise!: (value: CachedAudio | null) => void
  const promise = new Promise<CachedAudio | null>(resolve => {
    resolvePromise = resolve
  })
  const entry: BackgroundPreloadEntry = {
    promise,
    resolve: resolvePromise,
    started: false,
    priority: options.priority ?? Number.POSITIVE_INFINITY,
    refs: 1,
    abortHandlers: [],
    onDecoded: options.onDecoded ? [options.onDecoded] : [],
    run: () => {
      detachAbortHandlers(entry)
      entry.started = true
      backgroundPreloadsInFlight++
      const decode = useWebAudio
        ? preloadAudioFile(assetId, mediaUrl, { fallbackUrl: options.fallbackUrl })
        : preloadAudio(assetId, mediaUrl, undefined, { fromSeconds: options.fromSeconds }).then(
            result =>
              // A missing or malformed proxy still has a healthy original to fall back to.
              result ??
              (options.fallbackUrl && options.fallbackUrl !== mediaUrl
                ? (() => {
                    // The memo describes the proxy, not the original; the retry re-adds it if the original has no audio either.
                    videosWithoutAudio.delete(assetId)
                    return preloadAudio(assetId, options.fallbackUrl, undefined, { fromSeconds: options.fromSeconds })
                  })()
                : null),
          )
      let settled = false
      const finish = (result: CachedAudio | null) => {
        if (settled) return
        settled = true
        clearTimeout(deadline)
        backgroundPreloadsInFlight--
        backgroundPreloadEntries.delete(assetId)
        resolvePromise(result)
        pumpBackgroundPreloads()
      }
      // A stalled decode gives its slot back so later waveforms are not starved. It is not cancelled because
      // playback may share it via pendingDecodes, so concurrency can briefly exceed the cap; late completion
      // still reaches callers through onDecoded.
      const deadline = setTimeout(() => finish(null), BACKGROUND_PRELOAD_SLOT_DEADLINE_MS)
      decode
        .catch(() => null)
        .then(result => {
          // Callers hear about the real result even after the slot deadline resolved them null.
          if (result) for (const cb of entry.onDecoded) cb(result)
          finish(result)
        })
    },
  }
  backgroundPreloadEntries.set(assetId, entry)
  backgroundPreloadQueue.push(entry)
  if (options.signal) {
    const handler = () => releaseBackgroundPreload(assetId, entry)
    options.signal.addEventListener('abort', handler, { once: true })
    entry.abortHandlers.push({ signal: options.signal, handler })
  }
  pumpBackgroundPreloads()
  return promise
}

/**
 * Decode a pure audio file using Web Audio API's decodeAudioData.
 * This is the standard browser API for decoding audio files and works well
 * with MP3, M4A (AAC), WAV, OGG, FLAC, etc.
 *
 * IMPORTANT: Unlike video audio which is chunked for frame-level sync,
 * audio-only files are stored as a SINGLE buffer entry. This is much more
 * memory efficient and avoids scheduling hundreds of AudioBufferSourceNodes.
 */
async function decodeAudioFileViaWebAudio(
  assetId: string,
  audioUrl: string,
  options: {
    onProgress?: (progress: number) => void
    fallbackUrl?: string
  },
): Promise<CachedAudio | null> {
  try {
    const { onProgress, fallbackUrl } = options
    onProgress?.(0.1)

    const ctx = getAudioContext()
    const decodedBuffer = await decodeAudioWithFallback(audioUrl, fallbackUrl, async sourceUrl => {
      // Resolve each candidate through offline cache independently. Cache
      // identity includes the source URL, so a failed proxy cannot poison
      // the original-file retry.
      const effectiveUrl = await getSinkUrl(assetId, sourceUrl, {
        accessPattern: 'audio',
        allowBackgroundCache: false,
      })

      let response: Response
      try {
        response = await fetch(effectiveUrl)
      } catch (error) {
        if (!(error instanceof TypeError)) {
          throw error
        }

        const proxiedUrl = getFetchableStorageUrl(effectiveUrl)
        response = await fetch(proxiedUrl)
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`)
      }

      const arrayBuffer = await response.arrayBuffer()
      onProgress?.(0.3)
      return ctx.decodeAudioData(arrayBuffer)
    })
    onProgress?.(0.6)

    const duration = decodedBuffer.duration
    const sampleRate = decodedBuffer.sampleRate
    const channels = decodedBuffer.numberOfChannels

    // Create a COPY of the decoded buffer for forward playback FIRST
    // This must happen before any processing that might modify decodedBuffer
    const forwardBuffer = ctx.createBuffer(channels, decodedBuffer.length, sampleRate)
    for (let ch = 0; ch < channels; ch++) {
      const src = decodedBuffer.getChannelData(ch)
      const dest = forwardBuffer.getChannelData(ch)
      dest.set(src)
    }

    const memoryBytes = estimateAudioBufferMemory(forwardBuffer)

    // For audio-only files, store the FULL buffer as a single entry
    // This is much more efficient than chunking for pure audio playback:
    // - No memory overhead from hundreds of chunk objects
    // - Single AudioBufferSourceNode per playback instead of hundreds
    // - Uses source.start(when, offset, duration) for portion playback
    const buffers: AudioBufferEntry[] = [
      {
        buffer: forwardBuffer,
        timestamp: 0,
        duration: duration,
      },
    ]

    onProgress?.(0.7)

    // Initialize with empty reversed buffers and tempo buffers
    // Forward playback at 1x works immediately; reverse and 2x are deferred
    const reversedBuffers: AudioBufferEntry[] = []
    const tempoBuffers = new Map<number, TempoBuffers>()

    const cached: CachedAudio = {
      buffers,
      reversedBuffers,
      tempoBuffers,
      sampleRate,
      channels,
      duration,
      memoryBytes,
    }

    // Store in cache immediately - forward 1x playback is ready
    audioCache.set(assetId, cached)
    notifyAudioCacheChanged()

    onProgress?.(0.9)

    // Defer ALL heavy processing to idle time to avoid blocking UI:
    // - Reversed buffer (for reverse playback at 1x)
    // - Tempo buffers (for 2x playback)
    scheduleHeavyAudioProcessing(assetId, forwardBuffer, true)

    onProgress?.(1)
    return cached
  } catch (error) {
    console.error(`[AudioCache] Failed to decode audio file: ${assetId}`, error)
    return null
  }
}

/**
 * Decode audio from an existing Mediabunny Input (e.g., from a warm video sink).
 * This avoids creating a new Input and re-fetching/parsing the container.
 *
 * @param videoId The video/asset ID
 * @param input The Mediabunny Input (owned by caller, will NOT be disposed)
 * @returns Cached audio or null if no audio/unsupported codec
 */
export async function decodeAudioFromInput(videoId: string, input: Input): Promise<CachedAudio | null> {
  ensureAudioCacheInitialized()

  // Check if already cached
  const existingCached = audioCache.get(videoId)
  if (existingCached) {
    return existingCached
  }

  // Skip videos known to have no audio track
  if (videosWithoutAudio.has(videoId)) {
    return null
  }

  // Check if already being decoded
  const pendingDecode = pendingDecodes.get(videoId)
  if (pendingDecode) {
    return pendingDecode
  }

  // Start decode - note: we do NOT dispose the input, caller owns it
  const decodePromise = decodeAudioFromInputInternal(videoId, input)
  pendingDecodes.set(videoId, decodePromise)

  try {
    return await decodePromise
  } finally {
    pendingDecodes.delete(videoId)
  }
}

/**
 * Internal: Decode audio from an existing Input without disposing it.
 */
async function decodeAudioFromInputInternal(videoId: string, input: Input): Promise<CachedAudio | null> {
  try {
    // Get primary audio track from the existing input
    const audioTrack = await input.getPrimaryAudioTrack()
    if (!audioTrack) {
      videosWithoutAudio.add(videoId)
      notifyAudioCacheChanged()
      return null
    }

    // Check if we can decode this codec
    const canDecode = await audioTrack.canDecode()
    if (!canDecode) {
      console.warn(`[AudioCache] Audio codec not supported for decoding: ${videoId}`)
      videosWithoutAudio.add(videoId)
      notifyAudioCacheChanged()
      return null
    }

    // Delegate to shared decode logic
    return await decodeAudioFromTrack(videoId, audioTrack)
  } catch (error) {
    if (isInputDisposedError(error)) {
      return null
    }
    console.error(`[AudioCache] Failed to decode audio from input: ${videoId}`, error)
    return null
  }
  // Note: We do NOT dispose the input - caller (SinkPool) owns it
}

/**
 * Tempos to pre-process during decode for pitch-preserved playback.
 * Only 2x is pre-processed since audio only plays at 1x and 2x speeds.
 */
// Transport 2× is the common case; clip tempos build on first use.
const PREPROCESS_TEMPOS = [2] as const

/**
 * Core audio decode logic - shared by both URL-based and Input-based decoding.
 * Extracts audio samples, creates buffers, and caches the result.
 */
async function decodeAudioFromTrack(
  videoId: string,
  audioTrack: AudioTrack,
  onProgress?: (progress: number) => void,
  fromSeconds: number = 0,
): Promise<CachedAudio | null> {
  // Get audio metadata
  const duration = await audioTrack.computeDuration()
  const sampleRate = audioTrack.sampleRate
  const channels = audioTrack.numberOfChannels

  // Create audio sample sink for extraction
  const sink = new AudioSampleSink(audioTrack)

  const partial: PartialAudio = { buffers: [], coverage: [], sampleRate, channels, duration }
  partialDecodes.set(videoId, partial)

  const appendSamples = async (start?: number, end?: number): Promise<void> => {
    for await (const audioSample of sink.samples(start, end)) {
      try {
        partial.buffers.push({
          buffer: audioSample.toAudioBuffer(),
          timestamp: audioSample.timestamp,
          duration: audioSample.duration,
        })
        mergeRange(partial.coverage, {
          start: audioSample.timestamp,
          end: audioSample.timestamp + audioSample.duration,
        })
        if (onProgress && duration > 0) {
          const coveredDuration = partial.coverage.reduce((total, range) => total + range.end - range.start, 0)
          onProgress(Math.min(0.9, (coveredDuration / duration) * 0.9))
        }
        notifyAudioProgress()
      } finally {
        audioSample.close()
      }
    }
  }

  try {
    await appendSamples(fromSeconds)
    // Backfill everything before the forward pass. It began at the chunk containing `fromSeconds`,
    // whose timestamp may sit before it, so the bound is that chunk's own timestamp — `end` is
    // exclusive, so no chunk decodes twice. An empty forward pass means the trim point lies past the
    // end of the audio, and "everything before it" is the whole track (no `end`).
    if (fromSeconds > 0) await appendSamples(0, partial.buffers[0]?.timestamp)
  } catch (error) {
    partialDecodes.delete(videoId)
    throw error
  }

  const buffers = partial.buffers.sort((a, b) => a.timestamp - b.timestamp)

  // Calculate base memory usage
  let memoryBytes = 0
  for (const entry of buffers) {
    memoryBytes += estimateAudioBufferMemory(entry.buffer)
  }

  // Initialize with empty tempo buffers - 1x playback works immediately
  const tempoBuffers = new Map<number, TempoBuffers>()

  const cached: CachedAudio = {
    buffers,
    reversedBuffers: [],
    tempoBuffers,
    sampleRate,
    channels,
    duration,
    memoryBytes,
  }

  // LRU cache will automatically evict if needed - 1x playback is ready
  audioCache.set(videoId, cached)
  notifyAudioCacheChanged()
  partialDecodes.delete(videoId)
  onProgress?.(1)

  // Create combined buffer for deferred tempo processing
  const ctx = getAudioContext()
  const combinedBuffer = combineBufferChunks(ctx, buffers, duration, sampleRate, channels)

  // Defer tempo processing to idle time to avoid blocking UI
  // 2x playback will work once this completes in the background
  scheduleTempoProcessing(videoId, combinedBuffer)

  return cached
}

/**
 * Reverse an AudioBuffer's sample data for reverse playback
 * Creates a new AudioBuffer with samples in reverse order
 */
function reverseAudioBuffer(buffer: AudioBuffer): AudioBuffer {
  const ctx = getAudioContext()
  const reversed = ctx.createBuffer(buffer.numberOfChannels, buffer.length, buffer.sampleRate)

  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const forward = buffer.getChannelData(channel)
    const backward = reversed.getChannelData(channel)
    for (let i = 0; i < buffer.length; i++) {
      backward[i] = forward[buffer.length - 1 - i]
    }
  }

  return reversed
}

export function getReversedBuffers(assetId: string, cached: CachedAudio): AudioBufferEntry[] {
  if (cached.reversedBuffers.length > 0 || cached.buffers.length === 0) return cached.reversedBuffers

  for (let index = cached.buffers.length - 1; index >= 0; index--) {
    const entry = cached.buffers[index]
    const reversedBuffer = reverseAudioBuffer(entry.buffer)
    cached.reversedBuffers.push({
      buffer: reversedBuffer,
      timestamp: cached.duration - entry.timestamp - entry.duration,
      duration: entry.duration,
    })
    cached.memoryBytes += estimateAudioBufferMemory(reversedBuffer)
  }
  audioCache.set(assetId, cached)
  notifyAudioCacheChanged()
  return cached.reversedBuffers
}

/**
 * Combine multiple AudioBuffer chunks into a single buffer.
 * Uses sequential concatenation to avoid gaps from timestamp precision issues.
 */
function combineBufferChunks(
  ctx: AudioContext,
  chunks: AudioBufferEntry[],
  _totalDuration: number,
  sampleRate: number,
  channels: number,
): AudioBuffer {
  // Sort chunks by timestamp to ensure correct order
  const sortedChunks = [...chunks].sort((a, b) => a.timestamp - b.timestamp)

  // Calculate total samples from actual buffer lengths (more accurate than duration)
  let totalSamples = 0
  for (const { buffer } of sortedChunks) {
    totalSamples += buffer.length
  }

  const combined = ctx.createBuffer(channels, totalSamples, sampleRate)

  // Concatenate sequentially - no gaps possible
  let writePosition = 0
  for (const { buffer } of sortedChunks) {
    for (let ch = 0; ch < channels; ch++) {
      const source = buffer.getChannelData(ch)
      const dest = combined.getChannelData(ch)
      dest.set(source, writePosition)
    }
    writePosition += buffer.length
  }

  return combined
}

/**
 * Calculate the RMS (Root Mean Square) of an AudioBuffer.
 * This measures average power/loudness for volume normalization.
 */
function calculateRMS(buffer: AudioBuffer): number {
  let sumOfSquares = 0

  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const data = buffer.getChannelData(ch)
    for (let i = 0; i < data.length; i++) {
      sumOfSquares += data[i] * data[i]
    }
  }

  // All channels have the same length, so total samples = length * channels
  const totalSamples = buffer.length * buffer.numberOfChannels
  return totalSamples > 0 ? Math.sqrt(sumOfSquares / totalSamples) : 0
}

/**
 * Process an AudioBuffer with SoundTouch for tempo change while preserving pitch
 */
function processBufferWithTempo(ctx: AudioContext, inputBuffer: AudioBuffer, tempo: number): AudioBuffer {
  if (tempo === 1) return inputBuffer

  // Measure input RMS for volume normalization after SoundTouch processing
  const inputRMS = calculateRMS(inputBuffer)

  const sampleRate = inputBuffer.sampleRate
  const channels = inputBuffer.numberOfChannels
  const inputFrames = inputBuffer.length

  // Output length depends on tempo (at 2x tempo, output is half length)
  // Add generous padding for SoundTouch's internal buffering and flush
  const SOUNDTOUCH_BUFFER_PADDING = 16384
  const expectedOutputFrames = Math.ceil(inputFrames / tempo) + SOUNDTOUCH_BUFFER_PADDING

  // Set up SoundTouch with quality settings optimized for the sample rate
  const soundTouch = new SoundTouch()
  soundTouch.tempo = tempo
  soundTouch.pitch = 1 // Preserve original pitch

  // Create source and filter using library's WebAudioBufferSource
  const source = new WebAudioBufferSource(inputBuffer)
  const filter = new SimpleFilter(source, soundTouch)

  // Process all samples - use larger chunks for smoother processing
  const outputSamples = new Float32Array(expectedOutputFrames * 2)
  let totalExtracted = 0

  // Larger chunk size reduces overhead and boundary artifacts
  const chunkSize = 8192
  const chunk = new Float32Array(chunkSize * 2)

  // Extract until no more samples
  let consecutiveZeros = 0
  const maxConsecutiveZeros = 3 // Allow a few empty extracts before giving up (flush latency)

  while (consecutiveZeros < maxConsecutiveZeros) {
    const extracted = filter.extract(chunk, chunkSize)

    if (extracted === 0) {
      consecutiveZeros++
      continue
    }
    consecutiveZeros = 0

    // Copy to output with bounds check
    const copyCount = Math.min(extracted * 2, outputSamples.length - totalExtracted * 2)
    for (let i = 0; i < copyCount; i++) {
      outputSamples[totalExtracted * 2 + i] = chunk[i]
    }
    totalExtracted += Math.floor(copyCount / 2)
  }

  // Guard against empty output
  if (totalExtracted === 0) {
    console.warn('[AudioCache] SoundTouch produced no output, returning original buffer')
    return inputBuffer
  }

  const outputBuffer = ctx.createBuffer(channels, totalExtracted, sampleRate)

  // Deinterleave back to separate channels
  const leftOut = outputBuffer.getChannelData(0)
  const rightOut = channels > 1 ? outputBuffer.getChannelData(1) : null

  for (let i = 0; i < totalExtracted; i++) {
    leftOut[i] = outputSamples[i * 2]
    if (rightOut) {
      rightOut[i] = outputSamples[i * 2 + 1]
    }
  }

  // Compensate for volume reduction caused by SoundTouch's WSOLA algorithm
  // Time-stretching causes phase interference when overlapping audio windows
  const outputRMS = calculateRMS(outputBuffer)
  if (outputRMS > 0 && inputRMS > 0) {
    // Cap gain at 2.0 (6dB) to prevent clipping
    const gainFactor = Math.min(inputRMS / outputRMS, 2.0)

    if (gainFactor > 1.01) {
      // Only apply if meaningful difference (>1%)
      for (let i = 0; i < totalExtracted; i++) {
        // Clamp to [-1.0, 1.0] to prevent clipping distortion
        leftOut[i] = Math.max(-1.0, Math.min(1.0, leftOut[i] * gainFactor))
        if (rightOut) {
          rightOut[i] = Math.max(-1.0, Math.min(1.0, rightOut[i] * gainFactor))
        }
      }
    }
  }

  // Apply short fades at start and end to prevent clicks (2ms each)
  const fadeSamples = Math.min(Math.floor(sampleRate * 0.002), Math.floor(totalExtracted / 4))

  // Fade in
  for (let i = 0; i < fadeSamples; i++) {
    const gain = i / fadeSamples
    leftOut[i] *= gain
    if (rightOut) {
      rightOut[i] *= gain
    }
  }

  // Fade out
  for (let i = 0; i < fadeSamples; i++) {
    const gain = 1 - i / fadeSamples
    const idx = totalExtracted - fadeSamples + i
    leftOut[idx] *= gain
    if (rightOut) {
      rightOut[idx] *= gain
    }
  }

  return outputBuffer
}

/**
 * Decode audio using Mediabunny from a URL.
 * Creates a new Input, decodes audio, then disposes the Input.
 */
async function decodeAudioViaMediAbunny(
  videoId: string,
  videoUrl: string,
  onProgress?: (progress: number) => void,
  fromSeconds: number = 0,
): Promise<CachedAudio | null> {
  let input: Input | null = null

  try {
    // Resolve URL through offline cache, but do not start full-file video
    // caching as a side effect of audio decode/preload work.
    const effectiveUrl = await getSinkUrl(videoId, videoUrl, {
      accessPattern: 'audio',
      allowBackgroundCache: false,
    })

    // Create Mediabunny input from URL
    input = new Input({
      formats: ALL_FORMATS,
      source: new UrlSource(effectiveUrl),
    })

    // Get primary audio track
    const audioTrack = await input.getPrimaryAudioTrack()
    if (!audioTrack) {
      videosWithoutAudio.add(videoId)
      notifyAudioCacheChanged()
      return null
    }

    // Check if we can decode this codec
    const canDecode = await audioTrack.canDecode()
    if (!canDecode) {
      console.warn(`[AudioCache] Audio codec not supported for decoding: ${videoId}`)
      videosWithoutAudio.add(videoId)
      notifyAudioCacheChanged()
      return null
    }

    // Delegate to shared decode logic
    return await decodeAudioFromTrack(videoId, audioTrack, onProgress, fromSeconds)
  } catch (error) {
    if (isInputDisposedError(error)) {
      return null
    }
    console.error(`[AudioCache] Failed to decode audio: ${videoId}`, error)
    return null
  } finally {
    // Clean up resources - we own this Input
    if (input) {
      input.dispose()
    }
  }
}

/**
 * Supported playback speeds for audio.
 * Audio plays at 1x and 2x. At higher speeds (4x, 8x, 16x, etc.) audio is muted.
 */
const SUPPORTED_AUDIO_SPEEDS = [1, 2] as const

/**
 * Schedule audio playback for a clip starting at a given time
 * @param videoId The video/asset ID
 * @param clipStartTime When the clip starts in the AudioContext timeline (seconds)
 * @param trimInSeconds Where to start in the source audio (seconds)
 * @param durationSeconds How long to play (seconds)
 * @param playbackSpeed Playback speed multiplier (default 1). Negative = reverse. Pitch is preserved via SoundTouch at 2x.
 * @param playerId Optional player instance ID for scoped audio control
 */
export function scheduleAudioPlayback(
  videoId: string,
  clipStartTime: number,
  trimInSeconds: number,
  durationSeconds: number,
  playbackSpeed: number = 1,
  playerId?: string,
): boolean {
  // Canvas and other direct consumers still require a complete cache entry.
  if (!hasAudioCache(videoId)) return false
  return (
    scheduleAudioPlaybackNodes(videoId, clipStartTime, trimInSeconds, durationSeconds, playbackSpeed, playerId).nodes
      .length > 0
  )
}

interface PlayableAudio {
  cached: CachedAudio | PartialAudio
  buffers: AudioBufferEntry[]
  complete: boolean
}

interface ScheduledAudioResult {
  nodes: AudioBufferSourceNode[]
  buffers: AudioBufferEntry[]
  complete: boolean
}

function getPlayableBuffers(videoId: string): PlayableAudio | null {
  const cached = audioCache.get(videoId)
  if (cached) return { cached, buffers: cached.buffers, complete: true }
  const partial = partialDecodes.get(videoId)
  if (!partial) return null
  return { cached: partial, buffers: [...partial.buffers].sort((a, b) => a.timestamp - b.timestamp), complete: false }
}

function scheduleAudioPlaybackNodes(
  videoId: string,
  clipStartTime: number,
  trimInSeconds: number,
  durationSeconds: number,
  playbackSpeed: number = 1,
  playerId?: string,
  alreadyScheduled?: ReadonlySet<number>,
  atContextTime?: number,
  envelope?: AudioEnvelope,
  clipRate = 1,
  preservePitch = false,
): ScheduledAudioResult {
  const absSpeed = Math.abs(playbackSpeed)

  // Only play audio at supported speeds (1x, 2x). Skip audio at 4x, 8x, etc.
  if (!(SUPPORTED_AUDIO_SPEEDS as readonly number[]).includes(absSpeed)) {
    return { nodes: [], buffers: [], complete: false }
  }

  ensureAudioCacheInitialized()

  const playable = getPlayableBuffers(videoId)
  if (!playable) {
    // Log only once per videoId per session to avoid hot path console spam
    if (!warnedMissingAudioIds.has(videoId)) {
      console.warn(`[AudioCache] No cached audio for: ${videoId}`)
      warnedMissingAudioIds.add(videoId)
    }
    return { nodes: [], buffers: [], complete: false }
  }

  const ctx = getAudioContext()
  const isReverse = playbackSpeed < 0
  // Every chunk is placed relative to one clock reading. The timeline scheduler passes the reading
  // its playhead was derived from, so chunks scheduled across passes share a time base to the
  // sample; a fresh `ctx.currentTime` per chunk can straddle a render quantum and open a seam.
  const startAt = atContextTime ?? ctx.currentTime

  if (isReverse && !playable.complete) {
    return { nodes: [], buffers: [], complete: false }
  }

  if (preservePitch && clipRate !== 1) {
    if (!playable.complete) return { nodes: [], buffers: [], complete: false }
    const cached = playable.cached as CachedAudio
    const tempoBuffer = requestTempoBuffer(videoId, clipRate * absSpeed, true)
    if (!tempoBuffer) return { nodes: [], buffers: [], complete: false }
    const nodes = scheduleTempoBufferPlayback(
      ctx,
      tempoBuffer,
      cached.duration,
      startAt,
      clipStartTime,
      trimInSeconds,
      // The chunk scheduler contract supplies source duration; tempo playback takes timeline duration.
      durationSeconds / clipRate,
      isReverse,
      clipRate * absSpeed,
      absSpeed,
      playerId,
      envelope,
    )
    return { nodes, buffers: nodes.length > 0 ? cached.buffers : [], complete: true }
  }

  if (clipRate !== 1) {
    return scheduleChunkBasedPlayback(
      ctx,
      videoId,
      playable,
      startAt + clipStartTime / absSpeed,
      trimInSeconds,
      durationSeconds,
      isReverse,
      playerId,
      alreadyScheduled,
      envelope,
      clipRate * absSpeed,
      absSpeed,
    )
  }

  // 1x speed: use chunk-based playback (most efficient)
  if (absSpeed === 1) {
    return scheduleChunkBasedPlayback(
      ctx,
      videoId,
      playable,
      startAt + clipStartTime,
      trimInSeconds,
      durationSeconds,
      isReverse,
      playerId,
      alreadyScheduled,
      envelope,
    )
  }

  // 2x speed: use pre-processed SoundTouch tempo buffer (pitch preserved)
  if (!playable.complete) return { nodes: [], buffers: [], complete: false }
  const cached = playable.cached as CachedAudio
  const tempoBuffer = requestTempoBuffer(videoId, 2, true)
  if (tempoBuffer) {
    const nodes = scheduleTempoBufferPlayback(
      ctx,
      tempoBuffer,
      cached.duration,
      startAt,
      clipStartTime,
      trimInSeconds,
      durationSeconds,
      isReverse,
      2,
      2,
      playerId,
      envelope,
    )
    return { nodes, buffers: nodes.length > 0 ? cached.buffers : [], complete: true }
  }

  // Tempo buffers are built in idle time after the decode; retry on the next scheduler pass.
  return { nodes: [], buffers: [], complete: false }
}

export interface AudioEnvelope {
  reverse?: boolean
  /** Points in seconds for scheduling. */
  volumePoints?: VolumePoint[]
  gain: number
  pan: number
  fadeInSec: number
  fadeOutSec: number
  clipDurationSec: number
  offsetIntoClipSec: number
}

function withoutFadesForReverse(envelope?: AudioEnvelope): AudioEnvelope | undefined {
  // ponytail: reverse playback uses constant gain because reversed fades need separate timeline semantics.
  return envelope ? { ...envelope, fadeInSec: 0, fadeOutSec: 0, reverse: true } : undefined
}

export interface EnvelopePoint {
  time: number
  value: number
  type: 'set' | 'linearRamp'
}

export function computeEnvelopeSchedule(envelope: AudioEnvelope, tempoRatio = 1): EnvelopePoint[] {
  const duration = Math.max(0, envelope.clipDurationSec)
  const { fadeIn: clampedIn, fadeOut: clampedOut } = clampAudioFades(envelope.fadeInSec, envelope.fadeOutSec, duration)
  const offset = Math.max(0, envelope.offsetIntoClipSec)
  const end = Math.max(0, duration - offset)
  const tailStart = duration - clampedOut - offset
  let startValue = clampedIn > 0 && offset < clampedIn ? envelope.gain * Math.min(1, offset / clampedIn) : envelope.gain
  if (clampedOut > 0 && tailStart <= 0) startValue = envelope.gain * Math.max(0, end / clampedOut)

  const points: EnvelopePoint[] = [{ time: 0, value: startValue, type: 'set' }]
  const head = Math.max(0, clampedIn - offset)
  if (head > 0) points.push({ time: head / tempoRatio, value: envelope.gain, type: 'linearRamp' })
  if (clampedOut > 0) {
    if (tailStart > 0) points.push({ time: tailStart / tempoRatio, value: envelope.gain, type: 'set' })
    points.push({ time: end / tempoRatio, value: 0, type: 'linearRamp' })
  }
  return points
}

/**
 * Gain (+ pan) node for one scheduled clip. `clipStartAt` is the absolute AudioContext time at
 * which the clip's audio begins — the same reading the sources are started against, so the
 * automation and the chunks share one time base.
 */
function createEnvelopeOutput(ctx: AudioContext, clipStartAt: number, tempoRatio: number, envelope?: AudioEnvelope) {
  if (!envelope) return getMasterGain()
  const gainNode = ctx.createGain()
  for (const point of computeEnvelopeSchedule(envelope, tempoRatio)) {
    const time = clipStartAt + point.time
    if (point.type === 'set') gainNode.gain.setValueAtTime(point.value, time)
    else gainNode.gain.linearRampToValueAtTime(point.value, time)
  }
  let output: AudioNode = gainNode
  if (envelope.volumePoints?.length) {
    const automation = ctx.createGain()
    for (const point of computeVolumeSchedule(envelope, tempoRatio)) {
      const time = clipStartAt + point.time
      if (point.type === 'set') automation.gain.setValueAtTime(point.value, time)
      else automation.gain.linearRampToValueAtTime(point.value, time)
    }
    gainNode.connect(automation)
    output = automation
  }
  if (envelope.pan !== 0) {
    const panner = ctx.createStereoPanner()
    panner.pan.value = envelope.pan
    output.connect(panner)
    panner.connect(getMasterGain())
  } else {
    output.connect(getMasterGain())
  }
  return gainNode
}

/**
 * Schedule playback using a pre-processed tempo buffer (pitch preserved)
 */
function scheduleTempoBufferPlayback(
  ctx: AudioContext,
  tempoBuffer: TempoBuffers,
  originalDuration: number,
  atContextTime: number,
  clipStartTime: number,
  trimInSeconds: number,
  durationSeconds: number,
  isReverse: boolean,
  sourceRate: number,
  timelineRate: number,
  playerId?: string,
  envelope?: AudioEnvelope,
): AudioBufferSourceNode[] {
  const buffer = isReverse ? tempoBuffer.reversedBuffer : tempoBuffer.buffer

  // Source positions and timeline durations use different clocks for retimed clips.
  const clipRate = sourceRate / timelineRate
  const originalTrimEnd = trimInSeconds + durationSeconds * clipRate
  let bufferTrimIn = isReverse ? (originalDuration - originalTrimEnd) / sourceRate : trimInSeconds / sourceRate
  let bufferDuration = durationSeconds / timelineRate

  // Clamp to buffer bounds
  bufferTrimIn = Math.max(0, Math.min(bufferTrimIn, buffer.duration))
  bufferDuration = Math.min(bufferDuration, buffer.duration - bufferTrimIn)

  if (bufferDuration <= 0) return []

  // Create and schedule the buffer source
  const source = ctx.createBufferSource()
  source.buffer = buffer
  const output = createEnvelopeOutput(
    ctx,
    atContextTime + clipStartTime / timelineRate,
    timelineRate,
    isReverse ? withoutFadesForReverse(envelope) : envelope,
  )
  source.connect(output)

  // The buffer already contains the tempo change; leave playbackRate at 1.
  source.start(atContextTime + clipStartTime / timelineRate, bufferTrimIn, bufferDuration)

  scheduledNodes.push(source)
  if (playerId) {
    const playerNodes = scheduledNodesByPlayer.get(playerId) ?? []
    playerNodes.push(source)
    scheduledNodesByPlayer.set(playerId, playerNodes)
  }
  return [source]
}

/**
 * Schedule chunk-based playback for 1x speed
 */
function scheduleChunkBasedPlayback(
  ctx: AudioContext,
  videoId: string,
  playable: PlayableAudio,
  clipStartAt: number, // absolute AudioContext time at which source offset `trimInSeconds` plays
  trimInSeconds: number,
  durationSeconds: number,
  isReverse: boolean,
  playerId?: string,
  alreadyScheduled?: ReadonlySet<number>,
  envelope?: AudioEnvelope,
  sourceRate = 1,
  envelopeRate = 1,
): ScheduledAudioResult {
  const trimEndSeconds = trimInSeconds + durationSeconds
  const cached = playable.cached
  // Each chunk is scheduled once, by identity of its source timestamp. A chunk that decodes after
  // the playhead passed its start gets `startInBuffer` > 0 below, so only its remainder plays, aligned.
  const bufferSet = isReverse
    ? getReversedBuffers(videoId, cached as CachedAudio)
    : playable.buffers.filter(entry => !alreadyScheduled?.has(entry.timestamp))
  const sources: AudioBufferSourceNode[] = []
  const buffers: AudioBufferEntry[] = []
  const output = createEnvelopeOutput(
    ctx,
    clipStartAt,
    envelopeRate,
    isReverse ? withoutFadesForReverse(envelope) : envelope,
  )

  if (isReverse) {
    const totalDuration = cached.duration
    const reverseTrimStart = totalDuration - trimEndSeconds
    const reverseTrimEnd = totalDuration - trimInSeconds

    for (const { buffer, timestamp, duration } of bufferSet) {
      const bufferEnd = timestamp + duration

      if (bufferEnd <= reverseTrimStart || timestamp >= reverseTrimEnd) {
        continue
      }

      const bufferOffsetInClip = Math.max(0, timestamp - reverseTrimStart)
      const startInBuffer = Math.max(0, reverseTrimStart - timestamp)
      const endInBuffer = Math.min(duration, reverseTrimEnd - timestamp)
      const playDuration = endInBuffer - startInBuffer

      if (playDuration <= 0) continue

      const source = ctx.createBufferSource()
      source.buffer = buffer
      if (sourceRate !== 1) source.playbackRate.value = sourceRate
      source.connect(output)

      source.start(clipStartAt + bufferOffsetInClip / sourceRate, startInBuffer, playDuration)

      scheduledNodes.push(source)
      sources.push(source)
      buffers.push({ buffer, timestamp, duration })
      if (playerId) {
        const playerNodes = scheduledNodesByPlayer.get(playerId) ?? []
        playerNodes.push(source)
        scheduledNodesByPlayer.set(playerId, playerNodes)
      }
    }
  } else {
    // Forward playback
    for (const { buffer, timestamp, duration } of bufferSet) {
      const bufferEnd = timestamp + duration

      if (bufferEnd <= trimInSeconds || timestamp >= trimEndSeconds) {
        continue
      }

      const bufferOffsetInClip = Math.max(0, timestamp - trimInSeconds)
      const startInBuffer = Math.max(0, trimInSeconds - timestamp)
      const endInBuffer = Math.min(duration, trimEndSeconds - timestamp)
      const playDuration = endInBuffer - startInBuffer

      if (playDuration <= 0) continue

      const source = ctx.createBufferSource()
      source.buffer = buffer
      if (sourceRate !== 1) source.playbackRate.value = sourceRate
      source.connect(output)

      source.start(clipStartAt + bufferOffsetInClip / sourceRate, startInBuffer, playDuration)

      scheduledNodes.push(source)
      sources.push(source)
      buffers.push({ buffer, timestamp, duration })
      if (playerId) {
        const playerNodes = scheduledNodesByPlayer.get(playerId) ?? []
        playerNodes.push(source)
        scheduledNodesByPlayer.set(playerId, playerNodes)
      }
    }
  }
  return { nodes: sources, buffers, complete: playable.complete }
}

function stopAndUntrackNodes(nodes: AudioBufferSourceNode[]): void {
  for (const node of nodes) {
    try {
      node.stop()
    } catch {
      // Already stopped
    }
    try {
      node.disconnect()
    } catch {
      // Already disconnected
    }
    const nodeIndex = scheduledNodes.indexOf(node)
    if (nodeIndex !== -1) scheduledNodes.splice(nodeIndex, 1)
  }
}

/**
 * Stop audio for a specific player instance
 */
export function stopAudioForPlayer(playerId: string): void {
  const playerNodes = scheduledNodesByPlayer.get(playerId)
  if (!playerNodes) return

  for (const node of playerNodes) {
    try {
      node.stop()
      node.disconnect()
    } catch {
      // Ignore errors from already-stopped nodes
    }
  }
  scheduledNodesByPlayer.delete(playerId)

  // Also remove from global list
  scheduledNodes = scheduledNodes.filter(n => !playerNodes.includes(n))
}

/**
 * Clean up audio nodes in the background.
 * Called via requestIdleCallback for deferred cleanup.
 */
function cleanupNodesInBackground(nodes: AudioBufferSourceNode[]): void {
  for (const node of nodes) {
    try {
      node.stop()
    } catch {
      // Ignore errors from already-stopped nodes
    }
    try {
      node.disconnect()
    } catch {
      // Ignore errors from already-disconnected nodes
    }
  }
}

/**
 * Stop all scheduled audio playback.
 *
 * Uses O(1) master gain disconnect for instant silence, then defers
 * per-node cleanup to requestIdleCallback to avoid blocking the main thread.
 */
export function stopAllAudio(): void {
  // Fast path: disconnect master gain for instant silence (O(1) instead of O(n))
  if (masterGainNode) {
    masterGainNode.disconnect()
    masterGainNode = null
  }

  // Capture nodes for deferred cleanup
  const nodesToClean = [...scheduledNodes]
  scheduledNodes = []
  scheduledNodesByPlayer.clear()

  // Clear windowed scheduling tracking
  scheduledClipKeys.clear()
  scheduledNodeInfos = []

  // Defer per-node cleanup to avoid blocking main thread
  if (nodesToClean.length > 0) {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => cleanupNodesInBackground(nodesToClean), { timeout: 1000 })
    } else {
      // Safari fallback
      setTimeout(() => cleanupNodesInBackground(nodesToClean), 100)
    }
  }
}

/**
 * Pause audio scheduling during resize/drag operations.
 * When paused, scheduleAudioInWindow returns early without scheduling any audio.
 * This prevents audio glitches and overlapping playback during clip manipulation.
 */
export function pauseAudioScheduling(): void {
  audioSchedulingPaused = true
}

/**
 * Resume audio scheduling after resize/drag operations complete.
 * Call this after the operation finishes to allow audio to be scheduled at new positions.
 */
export function resumeAudioScheduling(): void {
  audioSchedulingPaused = false
}

// =============================================================================
// Windowed Audio Scheduling (P0 optimization)
// =============================================================================

/**
 * Schedule audio for clips within a time window around the playhead.
 * Only schedules clips that haven't been scheduled yet.
 *
 * @param clips - Flattened clips with effectiveStartTicks, trimIn, duration, assetId
 * @param playheadSeconds - Current playhead position in seconds
 * @param windowSeconds - How far ahead/behind to schedule (default 10s)
 * @param lookaheadSeconds - Extra lookahead buffer for seamless playback (default 1s)
 * @param speed - Playback speed (negative for reverse)
 * @param ticksToSecondsConverter - Function to convert ticks to seconds
 */
export function scheduleAudioInWindow(
  clips: Array<{
    assetId: string
    speed?: number
    preservePitch?: boolean
    loop?: boolean
    trimOut?: number
    clipId?: string
    volumePoints?: VolumePoint[]
    effectiveStartTicks: number
    trimIn: number
    duration: number
    gainDb?: number
    fadeIn?: number
    fadeOut?: number
    pan?: number
  }>,
  playheadSeconds: number,
  windowSeconds: number = 10,
  lookaheadSeconds: number = 1,
  speed: number,
  ticksToSecondsConverter: (ticks: number) => number,
  /** The `ctx.currentTime` reading `playheadSeconds` was derived from; defaults to now. */
  atContextTime?: number,
): void {
  // Skip scheduling entirely during resize/drag operations
  // This prevents audio glitches and overlapping playback
  if (audioSchedulingPaused) {
    return
  }

  const absSpeed = Math.abs(speed)

  // Only schedule audio at supported speeds (1x, 2x). Skip entirely at 4x, 8x, etc.
  if (!(SUPPORTED_AUDIO_SPEEDS as readonly number[]).includes(absSpeed)) {
    return
  }

  const isReverse = speed < 0

  // Define window bounds
  const windowStart = playheadSeconds - windowSeconds
  const windowEnd = playheadSeconds + windowSeconds + lookaheadSeconds

  // Ensure audio context is ready (needed for scheduleAudioPlayback)
  getAudioContext()

  // Debug: track scheduling results
  let scheduledCount = 0
  let skippedCached = 0
  let skippedWindow = 0
  let skippedFailed = 0

  const expandedClips: Array<
    (typeof clips)[number] & {
      envelopeOffsetTicks?: number
      envelopeDurationTicks?: number
    }
  > = []
  for (const clip of clips) {
    if (!clip.loop) {
      expandedClips.push(clip)
      continue
    }
    const passTicks = ((clip.trimOut ?? clip.trimIn) - clip.trimIn) / (clip.speed ?? 1)
    if (!Number.isFinite(passTicks) || passTicks <= 0) continue
    const passClip = { ...clip }
    delete passClip.loop
    // Only the passes that touch the scheduling window are materialized: a long loop over a
    // short window would otherwise build thousands of pass objects on every scheduler tick.
    const clipStartSeconds = ticksToSecondsConverter(clip.effectiveStartTicks)
    const passSeconds = ticksToSecondsConverter(passTicks)
    const lastPassIndex = Math.ceil(clip.duration / passTicks) - 1
    if (passSeconds <= 0 || lastPassIndex < 0) continue
    const firstPass = Math.max(0, Math.floor((windowStart - clipStartSeconds) / passSeconds))
    const lastPass = Math.min(lastPassIndex, Math.floor((windowEnd - clipStartSeconds) / passSeconds))
    for (let pass = firstPass; pass <= lastPass; pass++) {
      const offset = pass * passTicks
      expandedClips.push({
        ...passClip,
        clipId: `${clip.clipId ?? clip.assetId}#${pass}`,
        effectiveStartTicks: clip.effectiveStartTicks + offset,
        duration: Math.min(passTicks, clip.duration - offset),
        envelopeOffsetTicks: offset,
        envelopeDurationTicks: clip.duration,
      })
    }
  }

  for (const clip of expandedClips) {
    const clipRate = clip.speed ?? 1
    const clipStartSeconds = ticksToSecondsConverter(clip.effectiveStartTicks)
    const clipDurationSeconds = ticksToSecondsConverter(clip.duration)
    const clipEndSeconds = clipStartSeconds + clipDurationSeconds
    const trimInSeconds = ticksToSecondsConverter(clip.trimIn)

    // Skip clips outside window
    if (clipEndSeconds < windowStart || clipStartSeconds > windowEnd) {
      skippedWindow++
      continue
    }

    // Create unique key for this clip instance (includes duration for resize detection)
    const clipKey = `${clip.clipId ?? clip.assetId}-${clip.effectiveStartTicks}-${clip.duration}-${clip.gainDb ?? 0}-${clip.fadeIn ?? 0}-${clip.fadeOut ?? 0}-${clip.pan ?? 0}-${JSON.stringify(clip.volumePoints ?? [])}-${clipRate}-${clip.envelopeDurationTicks ?? clip.duration}-${clip.preservePitch === true}`

    // Invalidate any orphaned audio nodes for this clip (same asset+position but different duration)
    // This handles clip resize during playback - stops old audio so new duration can be scheduled
    const clipPrefix = `${clip.clipId ?? clip.assetId}-${clip.effectiveStartTicks}-`
    for (const info of [...scheduledNodeInfos]) {
      if (info.clipKey.startsWith(clipPrefix) && info.clipKey !== clipKey) {
        stopAndUntrackNodes(info.nodes)
        scheduledClipKeys.delete(info.clipKey)
        scheduledNodeInfos = scheduledNodeInfos.filter(i => i !== info)
      }
    }

    const existingInfo = scheduledNodeInfos.find(info => info.clipKey === clipKey)

    // Progressive forward scheduling revisits a clip until its source range is complete.
    if (scheduledClipKeys.has(clipKey) && existingInfo?.complete) {
      skippedCached++
      continue
    }

    // Calculate scheduling parameters based on direction
    const audioEnvelope = (offsetIntoClipSec: number): AudioEnvelope => ({
      gain: 10 ** ((clip.gainDb ?? 0) / 20),
      pan: clip.pan ?? 0,
      fadeInSec: ticksToSecondsConverter(clip.fadeIn ?? 0),
      fadeOutSec: ticksToSecondsConverter(clip.fadeOut ?? 0),
      clipDurationSec: ticksToSecondsConverter(clip.envelopeDurationTicks ?? clip.duration),
      offsetIntoClipSec: ticksToSecondsConverter(clip.envelopeOffsetTicks ?? 0) + offsetIntoClipSec,
      volumePoints: clip.volumePoints?.map(point => ({ ...point, time: ticksToSecondsConverter(point.time) })),
    })

    if (isReverse) {
      // ponytail: reverse volumePoints on a loop are per-pass approximate.
      // For reverse playback, skip clips that haven't been reached yet
      if (clipStartSeconds >= playheadSeconds) continue

      let audioStartTime = 0
      let audioTrimIn = trimInSeconds
      let audioDuration = clipDurationSeconds
      let offsetFromClipStart = 0

      if (playheadSeconds < clipEndSeconds) {
        // Playhead is within clip
        offsetFromClipStart = playheadSeconds - clipStartSeconds
        audioTrimIn = trimInSeconds
        audioDuration = offsetFromClipStart
      } else {
        // Clip is fully behind playhead (timeline offset, scheduling fn converts to real time)
        audioStartTime = playheadSeconds - clipEndSeconds
      }

      if (audioDuration > 0) {
        const result = scheduleAudioPlaybackNodes(
          clip.assetId,
          audioStartTime,
          audioTrimIn,
          audioDuration * clipRate,
          speed,
          undefined,
          undefined,
          atContextTime,
          audioEnvelope(audioDuration),
          clipRate,
          clip.preservePitch === true,
        )
        // Only mark as scheduled if audio was actually scheduled (prevents skipping uncached clips)
        if (result.nodes.length > 0) {
          scheduledCount++
          scheduledClipKeys.add(clipKey)
          scheduledNodeInfos.push({
            nodes: result.nodes,
            clipKey,
            clipStartSeconds,
            clipEndSeconds,
            scheduledSourceTimestamps: new Set(),
            complete: true,
          })
        } else {
          skippedFailed++
        }
      }
    } else {
      // Forward playback
      if (clipEndSeconds <= playheadSeconds) continue

      let audioStartTime = 0
      let audioTrimIn = trimInSeconds
      let audioDuration = clipDurationSeconds
      let offsetFromClipStart = 0

      if (playheadSeconds > clipStartSeconds) {
        // Playhead is within clip
        offsetFromClipStart = playheadSeconds - clipStartSeconds
        audioTrimIn = trimInSeconds + offsetFromClipStart * clipRate
        audioDuration = clipDurationSeconds - offsetFromClipStart
      } else {
        // Clip is ahead of playhead (timeline offset, scheduling fn converts to real time)
        audioStartTime = clipStartSeconds - playheadSeconds
      }

      if (audioDuration > 0) {
        const result = scheduleAudioPlaybackNodes(
          clip.assetId,
          audioStartTime,
          audioTrimIn,
          audioDuration * clipRate,
          speed,
          undefined,
          existingInfo?.scheduledSourceTimestamps,
          atContextTime,
          audioEnvelope(offsetFromClipStart),
          clipRate,
          clip.preservePitch === true,
        )
        // A complete entry has offered every chunk it will ever have; a partial one is revisited each pass.
        const complete = result.complete

        if (existingInfo) {
          existingInfo.nodes.push(...result.nodes)
          for (const entry of result.buffers) existingInfo.scheduledSourceTimestamps.add(entry.timestamp)
          existingInfo.complete = complete
          if (result.nodes.length > 0) scheduledCount++
        } else if (result.nodes.length > 0) {
          scheduledCount++
          scheduledClipKeys.add(clipKey)
          scheduledNodeInfos.push({
            nodes: result.nodes,
            clipKey,
            clipStartSeconds,
            clipEndSeconds,
            scheduledSourceTimestamps: new Set(result.buffers.map(entry => entry.timestamp)),
            complete,
          })
        } else {
          skippedFailed++
        }
      }
    }
  }
}

/**
 * Clean up audio nodes for clips that are now outside the cleanup window.
 * This frees up resources as the playhead moves.
 *
 * @param playheadSeconds - Current playhead position in seconds
 * @param cleanupWindowSeconds - Distance beyond which to cleanup (default 15s)
 */
export function cleanupDistantNodes(playheadSeconds: number, cleanupWindowSeconds: number = 15): void {
  const toRemove: ScheduledNodeInfo[] = []

  for (const info of scheduledNodeInfos) {
    // If playhead is WITHIN the clip, never clean it up (it's actively playing)
    if (playheadSeconds >= info.clipStartSeconds && playheadSeconds <= info.clipEndSeconds) {
      continue
    }

    // Playhead is outside the clip - check distance to nearest edge
    const distance = Math.min(
      Math.abs(info.clipStartSeconds - playheadSeconds),
      Math.abs(info.clipEndSeconds - playheadSeconds),
    )

    if (distance > cleanupWindowSeconds) {
      toRemove.push(info)
    }
  }

  if (toRemove.length === 0) return

  // Remove from tracking
  for (const info of toRemove) {
    scheduledClipKeys.delete(info.clipKey)
    stopAndUntrackNodes(info.nodes)
  }

  // Filter out removed infos
  scheduledNodeInfos = scheduledNodeInfos.filter(info => !toRemove.includes(info))
}

export const __audioCacheTesting = {
  setTempoBuffer(videoId: string, tempo: number, buffer: AudioBuffer, reversedBuffer: AudioBuffer): void {
    audioCache.peek(videoId)?.tempoBuffers.set(Math.round(tempo * 10000) / 10000, { buffer, reversedBuffer })
  },
  setCachedAudio(videoId: string, buffers: AudioBufferEntry[]): void {
    ensureAudioCacheInitialized()
    partialDecodes.delete(videoId)
    audioCache.set(videoId, {
      buffers,
      reversedBuffers: [],
      tempoBuffers: new Map(),
      sampleRate: 48_000,
      channels: 2,
      duration: buffers.reduce((duration, entry) => Math.max(duration, entry.timestamp + entry.duration), 0),
      memoryBytes: 1,
    })
    notifyAudioCacheChanged()
  },
  setPartialAudio(videoId: string, buffers: AudioBufferEntry[], coverage: AudioRange[], duration?: number): void {
    ensureAudioCacheInitialized()
    audioCache.delete(videoId)
    partialDecodes.set(videoId, {
      buffers,
      coverage,
      sampleRate: 48_000,
      channels: 2,
      // Default past every range so the tail tolerance only applies when a test asks for it.
      duration:
        duration ??
        Math.max(0, ...coverage.map(range => range.end), ...buffers.map(entry => entry.timestamp + entry.duration)) + 1,
    })
  },
  reset(): void {
    audioContext = null
    masterGainNode = null
    scheduledNodesByPlayer.clear()
    scheduledNodes = []
    scheduledClipKeys.clear()
    scheduledNodeInfos = []
    audioSchedulingPaused = false
    pendingTempoBuilds.clear()
    pendingDecodes.clear()
    partialDecodes.clear()
    videosWithoutAudio.clear()
    if (audioProgressNotificationTimer) clearTimeout(audioProgressNotificationTimer)
    audioProgressNotificationTimer = null
    backgroundPreloadQueue.length = 0
    backgroundPreloadEntries.clear()
    backgroundPreloadsInFlight = 0
    if (audioCacheInitialized) {
      audioCache.clear()
      notifyAudioCacheChanged()
    }
  },
}

/**
 * Get cached audio buffers for waveform generation.
 * Returns null if audio not cached. Does NOT update LRU access time
 * (use peek to avoid cache churn from visualization).
 */
export function getCachedAudioForWaveform(videoId: string): {
  buffers: readonly { buffer: AudioBuffer; timestamp: number; duration: number }[]
  sampleRate: number
  channels: number
  duration: number
} | null {
  ensureAudioCacheInitialized()

  // Use peek() to avoid updating LRU access time for visualization-only access
  const cached = audioCache.peek(videoId)
  if (!cached) return null

  return {
    buffers: cached.buffers,
    sampleRate: cached.sampleRate,
    channels: cached.channels,
    duration: cached.duration,
  }
}

/** Seeking starts at the interpolated level; no replay from the beginning of the curve. */
export function computeVolumeSchedule(envelope: AudioEnvelope, tempoRatio = 1): EnvelopePoint[] {
  const points = envelope.volumePoints
  if (!points?.length) return []
  const offset = envelope.offsetIntoClipSec
  const result: EnvelopePoint[] = [{ time: 0, value: volumeGainAt(points, offset), type: 'set' }]
  if (envelope.reverse) {
    for (let i = points.length - 1; i >= 0; i--) {
      const point = points[i]
      if (point.time > offset) continue
      const time = (offset - point.time) / tempoRatio
      result.push({ time, value: volumeDbToGain(point.gainDb), type: point.curve === 'hold' ? 'set' : 'linearRamp' })
      if (i > 0 && points[i - 1].curve === 'hold')
        result.push({ time, value: volumeDbToGain(points[i - 1].gainDb), type: 'set' })
    }
    return result
  }
  for (let i = 0; i < points.length; i++) {
    const point = points[i]
    if (point.time <= offset || point.time > envelope.clipDurationSec) continue
    const previous = points[i - 1]
    const hold = !previous || previous.curve === 'hold'
    const time = (point.time - offset) / tempoRatio
    result.push({ time, value: volumeDbToGain(point.gainDb), type: hold ? 'set' : 'linearRamp' })
  }
  return result
}
